import { createScreenShell, createMenuHeader, createCrestButton, createPasteButton, el } from '@bn-games/ui-shell';
import { ROOM_CODE_ALPHABET, ROOM_CODE_LENGTH, sanitizeRoomCodeInput } from '@bn-games/network-protocol';
import { t } from '../../i18n/I18n.js';
import { roomErrorText } from '../roomErrorText.js';
import type { ChessMenuContext, MenuScreen } from '../types.js';

const JOIN_BUTTON_WIDTH = '10rem';

/** Join by typing or pasting a room code. The board mounts itself once the room is ready. */
export function createOnlineJoinScreen(): MenuScreen {
  let frame: HTMLElement | null = null;
  let codeBuffer = '';
  let slots: HTMLElement[] = [];
  let statusNode: HTMLElement | null = null;
  let joinSlot: HTMLElement | null = null;
  let teardown: (() => void) | null = null;
  let connecting = false;

  function paintSlots(): void {
    slots.forEach((slot, i) => {
      const ch = codeBuffer[i] ?? '';
      const active = !connecting && i === codeBuffer.length;
      slot.classList.toggle('code-slot--active', active);
      slot.replaceChildren();
      if (ch !== '') slot.appendChild(document.createTextNode(ch));
      else if (active) slot.appendChild(el('div', { class: 'code-slot__caret' }));
    });
  }

  function paintJoinButton(ctx: ChessMenuContext): void {
    if (joinSlot === null) return;
    joinSlot.replaceChildren();
    if (codeBuffer.length !== ROOM_CODE_LENGTH || connecting) return;
    const button = createCrestButton({ label: t('online.join.submit'), primary: true, small: true, onClick: () => submit(ctx) });
    button.style.width = JOIN_BUTTON_WIDTH;
    joinSlot.appendChild(button);
  }

  function paint(ctx: ChessMenuContext): void {
    if (statusNode === null) return;
    const info = ctx.online.getRoomInfo();
    statusNode.classList.remove('waiting-dots');
    if (info.state === 'connecting' || info.state === 'joining') {
      statusNode.textContent = t('online.status.joining');
      statusNode.classList.add('waiting-dots');
      connecting = true;
    } else if (info.state === 'error') {
      statusNode.textContent = roomErrorText(info, t('online.status.couldNotJoin'));
      connecting = false;
    } else {
      statusNode.textContent = codeBuffer.length < ROOM_CODE_LENGTH
        ? t('online.join.typeHint', { count: ROOM_CODE_LENGTH })
        : t('online.join.enterHint');
      connecting = false;
    }
    paintJoinButton(ctx);
    paintSlots();
  }

  function submit(ctx: ChessMenuContext): void {
    if (codeBuffer.length !== ROOM_CODE_LENGTH || connecting) return;
    connecting = true;
    ctx.online.joinRoom(codeBuffer);
    paint(ctx);
  }

  function leave(ctx: ChessMenuContext): void {
    ctx.online.leaveRoom();
    ctx.navigate('play');
  }

  function onKey(ctx: ChessMenuContext, ev: KeyboardEvent): void {
    if (connecting) return;
    if (ev.key === 'Enter') {
      // With no full code to submit, Enter belongs to whatever button has focus.
      if (codeBuffer.length !== ROOM_CODE_LENGTH) return;
      ev.preventDefault();
      submit(ctx);
    } else if (ev.key === 'Escape') {
      ev.preventDefault();
      leave(ctx);
    } else if (ev.key === 'Backspace') {
      ev.preventDefault();
      codeBuffer = codeBuffer.slice(0, -1);
      paint(ctx);
    } else {
      const ch = ev.key.toUpperCase();
      if (ch.length !== 1 || !ROOM_CODE_ALPHABET.includes(ch) || codeBuffer.length >= ROOM_CODE_LENGTH) return;
      codeBuffer += ch;
      paint(ctx);
    }
  }

  function setCode(ctx: ChessMenuContext, raw: string): boolean {
    const sanitized = sanitizeRoomCodeInput(raw);
    if (connecting || sanitized.length === 0) return false;
    codeBuffer = sanitized;
    paint(ctx);
    return true;
  }

  return {
    mount(root, ctx) {
      const shell = createScreenShell();
      frame = shell.frame;
      slots = Array.from({ length: ROOM_CODE_LENGTH }, () => el('div', { class: 'code-slot' }));
      statusNode = el('div', { class: 'online-hint' });
      joinSlot = el('div', { class: 'online-join-slot' });
      const paste = createPasteButton({
        label: t('online.join.paste'),
        sanitize: sanitizeRoomCodeInput,
        onPaste: (text) => {
          setCode(ctx, text);
        },
      });
      const center = el('div', { class: 'online-center' }, [
        el('div', { class: 'online-label', text: t('online.join.codeLabel') }),
        el('div', { class: 'code-slots' }, slots),
        el('div', { class: 'button-row' }, [paste]),
        statusNode,
        joinSlot,
      ]);
      const back = el('div', { class: 'button-row back-button-row' }, [
        createCrestButton({ label: t('common.back'), small: true, onClick: () => leave(ctx) }),
      ]);
      shell.body.append(createMenuHeader({ title: t('online.join.title') }), center, back);
      root.appendChild(frame);

      const keyHandler = (ev: KeyboardEvent): void => onKey(ctx, ev);
      const pasteHandler = (ev: ClipboardEvent): void => {
        if (setCode(ctx, ev.clipboardData?.getData('text') ?? '')) ev.preventDefault();
      };
      window.addEventListener('keydown', keyHandler);
      window.addEventListener('paste', pasteHandler);
      const unsubRoom = ctx.online.onRoomUpdate(() => paint(ctx));
      teardown = (): void => {
        window.removeEventListener('keydown', keyHandler);
        window.removeEventListener('paste', pasteHandler);
        unsubRoom();
      };
      paint(ctx);
    },
    unmount() {
      teardown?.();
      teardown = null;
      frame?.remove();
      frame = null;
      slots = [];
      statusNode = null;
      joinSlot = null;
      codeBuffer = '';
      connecting = false;
    },
  };
}
